import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Field, FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { createProject, getProjectById, updateProject } from '../../lib/api';
import { uploadImage } from '../../utils/imageUpload';

const ProjectSchema = Yup.object().shape({
  title: Yup.string().required('Başlık gereklidir'),
  description: Yup.string().required('Açıklama gereklidir'),
  location: Yup.string().required('Konum gereklidir'),
  completion_date: Yup.date().required('Tamamlanma tarihi gereklidir'),
  images: Yup.array().of(Yup.string()).min(1, 'En az bir resim gereklidir'),
  features: Yup.array().of(Yup.string()),
  specifications: Yup.array().of(
    Yup.object().shape({
      label: Yup.string().required('Etiket gereklidir'),
      value: Yup.string().required('Değer gereklidir'),
    }),
  ),
});

const createSlug = (title) =>
  title
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

const ProjectForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    title: '',
    slug: '',
    description: '',
    location: '',
    completion_date: '',
    images: [''],
    features: [''],
    specifications: [{ label: '', value: '' }],
    is_active: true,
  });

  const fetchProject = useCallback(async () => {
    try {
      const data = await getProjectById(id);
      if (data) {
        setInitialValues({
          ...data,
          slug: data.slug || '',
          completion_date: data.completion_date?.split('T')[0] || '',
          images: data.images?.length ? data.images : [''],
          features: data.features?.length ? data.features : [''],
          specifications: data.specifications?.length ? data.specifications : [{ label: '', value: '' }],
        });
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Proje yüklenirken bir hata oluştu');
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchProject();
    }
  }, [id, fetchProject]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);

      const formData = {
        title: values.title,
        slug: values.slug || createSlug(values.title),
        description: values.description,
        location: values.location,
        completion_date: new Date(values.completion_date).toISOString(),
        images: values.images.filter((img) => img.trim() !== ''),
        features: values.features.filter((item) => item.trim() !== ''),
        specifications: values.specifications.filter(
          (item) => item.label.trim() !== '' && item.value.trim() !== '',
        ),
        is_active: values.is_active,
      };

      if (id) {
        await updateProject(id, formData);
      } else {
        await createProject(formData);
      }

      toast.success(id ? 'Proje güncellendi' : 'Proje oluşturuldu');
      navigate('/admin/projects');
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error(error.message || 'Proje kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              {id ? 'Projeyi Düzenle' : 'Yeni Proje Ekle'}
            </h2>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={ProjectSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <Form className="p-6 space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900">Temel Bilgiler</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                          Proje Adı
                        </label>
                        <Field
                          type="text"
                          name="title"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                          onBlur={(e) => {
                            const nextSlug = createSlug(e.target.value);
                            if (!values.slug) {
                              setFieldValue('slug', nextSlug);
                            }
                          }}
                        />
                        {errors.title && touched.title && (
                          <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                          Konum
                        </label>
                        <Field
                          type="text"
                          name="location"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                        />
                        {errors.location && touched.location && (
                          <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                        Slug
                      </label>
                      <Field
                        type="text"
                        name="slug"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                      />
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Açıklama
                      </label>
                      <Field
                        as="textarea"
                        name="description"
                        rows={4}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                      />
                      {errors.description && touched.description && (
                        <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="completion_date" className="block text-sm font-medium text-gray-700">
                        Tamamlanma Tarihi
                      </label>
                      <Field
                        type="date"
                        name="completion_date"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                      />
                      {errors.completion_date && touched.completion_date && (
                        <p className="mt-1 text-sm text-red-600">{errors.completion_date}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">Proje Resimleri</h3>
                    <FieldArray name="images">
                      {({ push, remove }) => (
                        <div className="space-y-4">
                          {values.images.map((image, index) => (
                            <div key={index} className="flex items-center gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-4">
                                  {image && (
                                    <img
                                      src={image}
                                      alt={`Proje resmi ${index + 1}`}
                                      className="h-20 w-20 object-cover rounded"
                                    />
                                  )}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={async (e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        const publicUrl = await uploadImage(file, 'images', 'projects');
                                        setFieldValue(`images.${index}`, publicUrl);
                                      }
                                    }}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#dbc582] file:text-white hover:file:bg-[#c5b274]"
                                  />
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Sil
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => push('')}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#dbc582] hover:bg-[#c5b274]"
                          >
                            Resim Ekle
                          </button>
                        </div>
                      )}
                    </FieldArray>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">Özellikler</h3>
                    <FieldArray name="features">
                      {({ push, remove }) => (
                        <div className="space-y-4">
                          {values.features.map((feature, index) => (
                            <div key={index} className="flex items-center gap-4">
                              <Field
                                name={`features.${index}`}
                                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                                placeholder="Özellik ekleyin"
                              />
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Sil
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => push('')}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#dbc582] hover:bg-[#c5b274]"
                          >
                            Özellik Ekle
                          </button>
                        </div>
                      )}
                    </FieldArray>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">Teknik Özellikler</h3>
                    <FieldArray name="specifications">
                      {({ push, remove }) => (
                        <div className="space-y-4">
                          {values.specifications.map((spec, index) => (
                            <div key={index} className="flex items-center gap-4">
                              <div className="flex-1 grid grid-cols-2 gap-4">
                                <Field
                                  name={`specifications.${index}.label`}
                                  className="rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                                  placeholder="Özellik adı"
                                />
                                <Field
                                  name={`specifications.${index}.value`}
                                  className="rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                                  placeholder="Değer"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Sil
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => push({ label: '', value: '' })}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#dbc582] hover:bg-[#c5b274]"
                          >
                            Teknik Özellik Ekle
                          </button>
                        </div>
                      )}
                    </FieldArray>
                  </div>

                  <div>
                    <div className="flex items-center">
                      <Field
                        type="checkbox"
                        name="is_active"
                        className="h-4 w-4 text-[#dbc582] focus:ring-[#dbc582] border-gray-300 rounded"
                      />
                      <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                        Aktif
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate('/admin/projects')}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dbc582]"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || loading}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#dbc582] hover:bg-[#c5b274] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dbc582] disabled:opacity-50"
                  >
                    {isSubmitting || loading ? 'Kaydediliyor...' : id ? 'Güncelle' : 'Oluştur'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ProjectForm;
